import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  BalanceItem, BokkzJrLogic,
  ClientEntity, FormattedExplanation, Jaarrapportage,
  JaarrapportageEntity, Jaarrekening, TrialBalanceItem
} from "@snelstart-ventures/fiscaal/domain-logic";
import {BokkzAlgDataService, BokkzJrDataService} from "@snelstart-ventures/fiscaal/domain-logic";
import { SelectFormControlItem, SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { combineLatest, distinctUntilChanged, Observable, ReplaySubject, Subject, takeUntil } from 'rxjs';
import { SpecTrialbalanceComponent } from '../spec-trialbalance/spec-trialbalance.component';
import { SpecFormattedexplanationComponent } from '../spec-formattedexplanation/spec-formattedexplanation.component';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { SharedUiButtonModule } from '@snelstart/ui-button';
import { CommonModule, NgIf } from '@angular/common';

@Component({
  selector: 'snelstart-venture-fiscaal-feature-spec-onderhanden-projecten',
  templateUrl: './spec-onderhanden-projecten.component.html',
  imports: [
    SpecTrialbalanceComponent,
    SpecFormattedexplanationComponent,
    SharedUiDetailsModule,
    ReactiveFormsModule,
    SharedUiButtonModule,
    SharedUiFormControlsModule,
    CommonModule
  ],
  styleUrls: ['./spec-onderhanden-projecten.component.css']
})
export class SpecOnderhandenProjectenComponent implements OnInit,OnChanges,OnDestroy {

  @Input() public id!: string;
  @Input() selectedTabObservable!: Observable<number>;
  private selectedTabSubject = new ReplaySubject<number>(1);
  showCustomControls = true;
  private destroy$ = new Subject<void>();
  public selectedTab = 0;
  public index = 0;
  public dialogVisible = false;
  public client = {} as ClientEntity;
  public jrEntity = {} as JaarrapportageEntity;
  public isRechtspersoon = false;

  public constructionContractsAssetsFg = {} as FormGroup;
  public constructionContractsAssetsDisclosureFg = {} as FormGroup;
  public constructionContractsNoLegalOwnershipDescriptionFg = {} as FormGroup;

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    {value: true, label: "Ja"},
    {value: false, label: "Nee"},
  ];

  public showDialog(input: number) {
    this.index = input
    this.dialogVisible = true;
  }

  constructor(
    public algDataService: BokkzAlgDataService,
    private cdRef: ChangeDetectorRef,
    private jrLogicService: BokkzJrLogic,
    public jrDataService: BokkzJrDataService) {
    this.createFormGroups();
  }

  ngOnInit(): void {
    if (this.selectedTabObservable) {
      this.selectedTabObservable
        .pipe(takeUntil(this.destroy$))
        .subscribe(tab => {
          this.selectedTabSubject.next(tab);
        });
    }

    combineLatest([
      this.selectedTabSubject.pipe(distinctUntilChanged()),
      this.jrDataService.jaarrapportageEntity
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([tab, jrEntity]) => {
        this.selectedTab = tab;
        this.jrEntity = jrEntity;
        this.patchData(jrEntity);
      });

    this.jrDataService.isRechtspersoon.subscribe((value) => {
      this.isRechtspersoon = value;
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedTabObservable']) {
      this.selectedTabObservable = changes['selectedTabObservable'].currentValue;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public createFormGroups() {
    this.constructionContractsAssetsFg = this.jrDataService.createFormGroupBalanceItem();
    this.constructionContractsAssetsDisclosureFg = this.jrDataService.createFormGroupFormattedExplanation();
    this.constructionContractsNoLegalOwnershipDescriptionFg = this.jrDataService.createFormGroupFormattedExplanation();
  }

  private patchData(value: JaarrapportageEntity) {
    const item = value.rapportage?.jaarrekeningen?.at(0)?.balans?.onderhandenProjecten;
    const text = value.rapportage?.jaarrekeningen?.at(0)?.balans?.onderhandenProjecten?.onderhandenProjectenTekstueel;
    if (item) {
      if (item.constructionContractsAssets) {
        const subItem = item.constructionContractsAssets;
        this.constructionContractsAssetsFg.patchValue(subItem)
        this.updateTrialBalanceItems(subItem.trialBalanceItems!, this.constructionContractsAssetsFg.get('trialBalanceItems') as FormArray)
      }
    }
    if (text) {
      if (text.constructionContractsAssetsDisclosure) {
        this.constructionContractsAssetsDisclosureFg.patchValue(text.constructionContractsAssetsDisclosure);
      }
      if (text.constructionContractsNoLegalOwnershipDescription) {
        this.constructionContractsNoLegalOwnershipDescriptionFg.patchValue(text.constructionContractsNoLegalOwnershipDescription);
      }
    }
  }

  updateTrialBalanceItems(lijst?: TrialBalanceItem[], formArray?: FormArray) {
    if (lijst && formArray) {
      for (let i = 0; i < lijst.length; i++) {
        if (i < formArray.length) {
          const control = formArray.at(i) as FormGroup;
          control.patchValue(lijst[i], {emitEvent: false});
        } else {
          const formGroup = this.jrDataService.createFormGroupTrialBalanceItem(lijst[i])
          formArray.push(formGroup, {emitEvent: false});
        }
      }
    }
  }

  saveForm(): void {
    this.showCustomControls = false;
    this.cdRef.detectChanges();
    setTimeout(() => {
      this.showCustomControls = true;
      this.cdRef.detectChanges();
    });
    this.constructionContractsAssetsFg.updateValueAndValidity({ emitEvent: false });
    this.constructionContractsAssetsDisclosureFg.updateValueAndValidity({ emitEvent: false });
    this.constructionContractsNoLegalOwnershipDescriptionFg.updateValueAndValidity({ emitEvent: false });

    if (this.jrEntity && this.jrEntity.rapportage && this.jrEntity.rapportage.jaarrekeningen) {
      this.jrEntity.rapportage.jaarrekeningen[0] = {
        ...this.jrEntity.rapportage.jaarrekeningen[0],
        balans: {
          ...this.jrEntity.rapportage.jaarrekeningen[0].balans,
          onderhandenProjecten: {
            ...this.jrEntity.rapportage.jaarrekeningen[0].balans?.onderhandenProjecten,
            constructionContractsAssets: this.constructionContractsAssetsFg.value,
            onderhandenProjectenTekstueel: {
              ...this.jrEntity.rapportage.jaarrekeningen[0].balans?.onderhandenProjecten?.onderhandenProjectenTekstueel,
              constructionContractsAssetsDisclosure: this.constructionContractsAssetsDisclosureFg.value,
              constructionContractsNoLegalOwnershipDescription: this.constructionContractsNoLegalOwnershipDescriptionFg.value
            }
          }
        }
      } as Jaarrekening
    }

    this.jrEntity = {
      ...this.jrEntity,
      rapportage: {
        ...this.jrEntity.rapportage,
        jaarrekeningen: [this.jrEntity.rapportage?.jaarrekeningen?.at(0)]
      }
    } as Jaarrapportage
    this.jrDataService.updateEntity(this.jrEntity);
    this.jrLogicService.getCalculation(this.jrEntity)
  }
}
